// SPDX-License-Identifier: AGPL-3.0-or-later

import * as PollCommands from '@app/features/channel/commands/PollCommands';
import type {PollResponse, PollStatusResponse} from '@fluxer/schema/src/domains/message/PollSchemas';
import {action, makeObservable, observable} from 'mobx';

interface PollOverride {
	base: PollResponse;
	poll: PollResponse;
}

// Keeps what the current member chose in each poll, plus the results returned by their own vote.
// Results from the gateway live on the message; an override is dropped as soon as the message carries newer results.
export class PollVotesStore {
	version = 0;
	private readonly mine = new Map<string, ReadonlyArray<number>>();
	private readonly overrides = new Map<string, PollOverride>();
	private readonly loading = new Set<string>();

	constructor() {
		makeObservable(this, {version: observable, bump: action});
	}

	bump(): void {
		this.version++;
	}

	getMine(messageId: string): ReadonlyArray<number> | undefined {
		void this.version;
		return this.mine.get(messageId);
	}

	getEffectivePoll(messageId: string, base: PollResponse): PollResponse {
		void this.version;
		const override = this.overrides.get(messageId);
		return override && override.base === base ? override.poll : base;
	}

	setMine(messageId: string, answerIds: ReadonlyArray<number>): void {
		this.mine.set(messageId, answerIds);
		this.bump();
	}

	applyStatus(messageId: string, base: PollResponse, status: PollStatusResponse): void {
		const {my_answer_ids: mine, ...poll} = status;
		this.mine.set(messageId, mine);
		this.overrides.set(messageId, {base, poll});
		this.bump();
	}

	async load(channelId: string, messageId: string): Promise<void> {
		if (this.mine.has(messageId) || this.loading.has(messageId)) return;
		this.loading.add(messageId);
		try {
			const status = await PollCommands.fetchPollStatus(channelId, messageId);
			this.mine.set(messageId, status.my_answer_ids);
			this.bump();
		} catch {
			// The poll still renders from the message; only the highlighted choice is missing.
		} finally {
			this.loading.delete(messageId);
		}
	}
}

export default new PollVotesStore();
